import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { 
    Bell,
    BellOff,
    Clock,
    Plus,
    Trash2,
    Check,
    X,
    Pill,
    Calendar,
    Loader2,
    AlarmClock,
    CheckCircle2,
    Circle,
    Timer,
    ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' },
];

const PRESET_TIMES = [
    { time: '07:00', label: 'Morning' },
    { time: '08:00', label: 'With Breakfast' },
    { time: '12:00', label: 'Noon' },
    { time: '13:00', label: 'With Lunch' },
    { time: '18:00', label: 'Evening' },
    { time: '19:00', label: 'With Dinner' },
    { time: '21:00', label: 'Bedtime' },
];

export default function MedicationReminders() {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('today');
    const [loading, setLoading] = useState(true);
    
    // Today's reminders
    const [todayReminders, setTodayReminders] = useState(null);
    
    // All reminders
    const [reminders, setReminders] = useState([]);
    
    // Supplements for setup
    const [supplements, setSupplements] = useState([]);
    
    // Weekly schedule
    const [weeklySchedule, setWeeklySchedule] = useState(null);
    
    // Setup form
    const [selectedSupplement, setSelectedSupplement] = useState('');
    const [reminderTimes, setReminderTimes] = useState([]);
    const [selectedDays, setSelectedDays] = useState(DAYS_OF_WEEK.map(d => d.value));
    const [newTime, setNewTime] = useState('08:00');
    const [newTimeLabel, setNewTimeLabel] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchTodayReminders = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/reminders/due/today`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setTodayReminders(data);
            }
        } catch (error) {
            console.error('Failed to fetch today reminders:', error);
        }
    }, [token]);

    const fetchReminders = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/reminders`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setReminders(data || []);
            }
        } catch (error) {
            console.error('Failed to fetch reminders:', error);
        }
    }, [token]);

    const fetchSupplements = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/supplements`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSupplements(data.filter(s => s.active) || []);
            }
        } catch (error) {
            console.error('Failed to fetch supplements:', error);
        }
    }, [token]);

    const fetchWeeklySchedule = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/reminders/schedule/week`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setWeeklySchedule(data);
            }
        } catch (error) {
            console.error('Failed to fetch weekly schedule:', error);
        }
    }, [token]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([
                fetchTodayReminders(),
                fetchReminders(),
                fetchSupplements(),
                fetchWeeklySchedule()
            ]);
            setLoading(false);
        };
        loadData();
    }, [fetchTodayReminders, fetchReminders, fetchSupplements, fetchWeeklySchedule]);

    const handleMarkTaken = async (supplementId) => {
        try {
            const res = await fetch(`${API_URL}/api/reminders/mark-taken`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ supplement_id: supplementId })
            });
            
            if (res.ok) {
                toast.success('Marked as taken!');
                fetchTodayReminders();
            }
        } catch (error) {
            toast.error('Failed to mark as taken');
        }
    };

    const handleSnooze = async (supplementId, minutes = 30) => {
        try {
            const res = await fetch(`${API_URL}/api/reminders/snooze`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ supplement_id: supplementId, minutes })
            });
            
            if (res.ok) {
                toast.success(`Snoozed for ${minutes} minutes`);
                fetchTodayReminders();
            }
        } catch (error) {
            toast.error('Failed to snooze');
        }
    };

    const addReminderTime = () => {
        if (!newTime) return;
        
        // Check for duplicate
        if (reminderTimes.some(t => t.time === newTime)) {
            toast.error('This time is already added');
            return;
        }
        
        setReminderTimes([...reminderTimes, { time: newTime, label: newTimeLabel || null }]);
        setNewTime('08:00');
        setNewTimeLabel('');
    };

    const removeReminderTime = (time) => {
        setReminderTimes(reminderTimes.filter(t => t.time !== time));
    };

    const toggleDay = (day) => {
        if (selectedDays.includes(day)) {
            setSelectedDays(selectedDays.filter(d => d !== day));
        } else {
            setSelectedDays([...selectedDays, day]);
        }
    };

    const handleSaveReminder = async () => {
        if (!selectedSupplement) {
            toast.error('Please select a supplement');
            return;
        }
        if (reminderTimes.length === 0) {
            toast.error('Please add at least one reminder time');
            return;
        }
        if (selectedDays.length === 0) {
            toast.error('Please select at least one day');
            return;
        }
        
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/reminders`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    supplement_id: selectedSupplement,
                    enabled: true,
                    reminder_times: reminderTimes,
                    days_of_week: selectedDays
                })
            });
            
            if (res.ok) {
                toast.success('Reminder saved!');
                setSelectedSupplement('');
                setReminderTimes([]);
                setSelectedDays(DAYS_OF_WEEK.map(d => d.value));
                fetchReminders();
                fetchTodayReminders();
                fetchWeeklySchedule();
            } else {
                toast.error('Failed to save reminder');
            }
        } catch (error) {
            toast.error('Failed to save reminder');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteReminder = async (supplementId) => {
        try {
            const res = await fetch(`${API_URL}/api/reminders/${supplementId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                toast.success('Reminder deleted');
                fetchReminders();
                fetchTodayReminders();
                fetchWeeklySchedule();
            }
        } catch (error) {
            toast.error('Failed to delete reminder');
        }
    };

    const formatTime12h = (time24) => {
        const [hours, minutes] = time24.split(':');
        const h = parseInt(hours);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${minutes} ${ampm}`;
    };

    // Get supplements that don't have reminders yet
    const supplementsWithoutReminders = supplements.filter(
        s => !reminders.some(r => r.supplement_id === s.id)
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                    <Bell className="w-8 h-8 text-amber-500" />
                    Medication Reminders
                </h1>
                <p className="text-muted-foreground mt-1">Set up reminders to never miss your supplements or medications</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="today" className="gap-2">
                        <AlarmClock className="w-4 h-4" />
                        Today
                        {todayReminders?.total_due > 0 && (
                            <Badge className="ml-1 bg-amber-500">
                                {todayReminders.total_due - todayReminders.total_taken}
                            </Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="schedule">
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule
                    </TabsTrigger>
                    <TabsTrigger value="setup">
                        <Plus className="w-4 h-4 mr-2" />
                        Setup
                    </TabsTrigger>
                </TabsList>

                {/* Today's Reminders */}
                <TabsContent value="today" className="mt-6 space-y-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                        </div>
                    ) : !todayReminders || (todayReminders.due.length === 0 && todayReminders.upcoming.length === 0) ? (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <BellOff className="w-12 h-12 text-muted-foreground/50 mb-4" />
                                <h3 className="font-medium text-lg mb-2">No Reminders Set</h3>
                                <p className="text-muted-foreground text-center max-w-md">
                                    Set up reminders to get notified when it's time to take your supplements or medications.
                                </p>
                                <Button className="mt-4" onClick={() => setActiveTab('setup')}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add Reminder
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <>
                            {/* Progress Summary */}
                            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20 border-amber-200 dark:border-amber-800">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Today's Progress</p>
                                            <p className="text-2xl font-bold">
                                                {todayReminders.total_taken} / {todayReminders.total_due}
                                            </p>
                                        </div>
                                        <div className="w-16 h-16 rounded-full bg-white dark:bg-gray-900 flex items-center justify-center shadow-lg">
                                            {todayReminders.total_taken === todayReminders.total_due && todayReminders.total_due > 0 ? (
                                                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                                            ) : (
                                                <Pill className="w-8 h-8 text-amber-500" />
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Due Now */}
                            {todayReminders.due.length > 0 && (
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg flex items-center gap-2">
                                        <Clock className="w-5 h-5 text-amber-500" />
                                        Due Now
                                    </h3>
                                    {todayReminders.due.map((reminder) => (
                                        <Card 
                                            key={reminder.id} 
                                            className={reminder.taken ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800' : 'border-amber-200 dark:border-amber-800'}
                                            data-testid={`reminder-${reminder.id}`}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                            reminder.taken 
                                                                ? 'bg-emerald-100 dark:bg-emerald-900/50' 
                                                                : 'bg-amber-100 dark:bg-amber-900/50'
                                                        }`}>
                                                            {reminder.taken ? (
                                                                <Check className="w-5 h-5 text-emerald-600" />
                                                            ) : (
                                                                <Pill className={`w-5 h-5 ${reminder.type === 'medication' ? 'text-blue-500' : 'text-amber-500'}`} />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium">{reminder.supplement_name}</p>
                                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                                <span>{formatTime12h(reminder.time)}</span>
                                                                {reminder.label && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>{reminder.label}</span>
                                                                    </>
                                                                )}
                                                                {reminder.dosage && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>{reminder.dosage}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {!reminder.taken && (
                                                        <div className="flex gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => handleSnooze(reminder.supplement_id, 30)}
                                                            >
                                                                <Timer className="w-4 h-4 mr-1" />
                                                                Snooze
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleMarkTaken(reminder.supplement_id)}
                                                            >
                                                                <Check className="w-4 h-4 mr-1" />
                                                                Take
                                                            </Button>
                                                        </div>
                                                    )}
                                                    
                                                    {reminder.taken && (
                                                        <Badge className="bg-emerald-100 text-emerald-700">
                                                            <Check className="w-3 h-3 mr-1" />
                                                            Taken
                                                        </Badge>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}

                            {/* Upcoming */}
                            {todayReminders.upcoming.length > 0 && (
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg flex items-center gap-2">
                                        <ChevronRight className="w-5 h-5" />
                                        Coming Up
                                    </h3>
                                    {todayReminders.upcoming.map((reminder) => (
                                        <Card key={reminder.id} className="bg-muted/30">
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                                                            <Circle className="w-5 h-5 text-muted-foreground" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium">{reminder.supplement_name}</p>
                                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                                <span>{formatTime12h(reminder.time)}</span>
                                                                {reminder.label && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>{reminder.label}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Badge variant="outline">{formatTime12h(reminder.time)}</Badge>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </TabsContent>

                {/* Weekly Schedule */}
                <TabsContent value="schedule" className="mt-6">
                    {!weeklySchedule ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin" />
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {DAYS_OF_WEEK.map(day => {
                                const daySchedule = weeklySchedule[day.value] || [];
                                const isToday = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() === day.value;
                                
                                return (
                                    <Card key={day.value} className={isToday ? 'ring-2 ring-amber-500' : ''}>
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base flex items-center gap-2">
                                                {day.label}
                                                {isToday && <Badge className="bg-amber-500 text-xs">Today</Badge>}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            {daySchedule.length === 0 ? (
                                                <p className="text-sm text-muted-foreground">No reminders</p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {daySchedule.map((item, idx) => (
                                                        <Badge key={idx} variant="outline" className="gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {formatTime12h(item.time)} - {item.supplement_name}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                {/* Setup */}
                <TabsContent value="setup" className="mt-6 space-y-6">
                    {/* Add New Reminder */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Add New Reminder</CardTitle>
                            <CardDescription>Set up a reminder for a supplement or medication</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Select Supplement */}
                            <div className="space-y-2">
                                <Label>Select Supplement/Medication</Label>
                                <Select value={selectedSupplement} onValueChange={setSelectedSupplement}>
                                    <SelectTrigger data-testid="supplement-select">
                                        <SelectValue placeholder="Choose a supplement..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {supplementsWithoutReminders.length === 0 ? (
                                            <SelectItem value="none" disabled>All supplements have reminders</SelectItem>
                                        ) : (
                                            supplementsWithoutReminders.map(s => (
                                                <SelectItem key={s.id} value={s.id}>
                                                    {s.name} {s.dosage && `(${s.dosage})`}
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Reminder Times */}
                            <div className="space-y-3">
                                <Label>Reminder Times</Label>
                                
                                {/* Current times */}
                                {reminderTimes.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {reminderTimes.map(t => (
                                            <Badge key={t.time} variant="secondary" className="gap-1 pr-1">
                                                {formatTime12h(t.time)}
                                                {t.label && ` (${t.label})`}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-4 w-4 ml-1"
                                                    onClick={() => removeReminderTime(t.time)}
                                                >
                                                    <X className="h-3 w-3" />
                                                </Button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                                
                                {/* Add time */}
                                <div className="flex flex-wrap gap-2">
                                    <Input
                                        type="time"
                                        value={newTime}
                                        onChange={(e) => setNewTime(e.target.value)}
                                        className="w-32"
                                    />
                                    <Input
                                        placeholder="Label (optional)"
                                        value={newTimeLabel}
                                        onChange={(e) => setNewTimeLabel(e.target.value)}
                                        className="w-40"
                                    />
                                    <Button variant="outline" onClick={addReminderTime}>
                                        <Plus className="w-4 h-4 mr-1" />
                                        Add
                                    </Button>
                                </div>
                                
                                {/* Quick add presets */}
                                <div className="flex flex-wrap gap-2 pt-2">
                                    {PRESET_TIMES.map(preset => (
                                        <Button
                                            key={preset.time}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                if (!reminderTimes.some(t => t.time === preset.time)) {
                                                    setReminderTimes([...reminderTimes, preset]);
                                                }
                                            }}
                                            disabled={reminderTimes.some(t => t.time === preset.time)}
                                        >
                                            {preset.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            {/* Days of Week */}
                            <div className="space-y-3">
                                <Label>Days of Week</Label>
                                <div className="flex flex-wrap gap-2">
                                    {DAYS_OF_WEEK.map(day => (
                                        <Button
                                            key={day.value}
                                            variant={selectedDays.includes(day.value) ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => toggleDay(day.value)}
                                        >
                                            {day.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            {/* Save Button */}
                            <Button onClick={handleSaveReminder} disabled={saving} className="w-full">
                                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                                {saving ? 'Saving...' : 'Save Reminder'}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Existing Reminders */}
                    {reminders.length > 0 && (
                        <div className="space-y-4">
                            <h3 className="font-semibold text-lg">Existing Reminders</h3>
                            {reminders.map(reminder => (
                                <Card key={reminder.supplement_id}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">{reminder.supplement_name}</p>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {reminder.reminder_times?.map((t, idx) => (
                                                        <Badge key={idx} variant="outline" className="text-xs">
                                                            {formatTime12h(t.time)}
                                                        </Badge>
                                                    ))}
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {reminder.days_of_week?.length === 7 
                                                        ? 'Every day' 
                                                        : reminder.days_of_week?.map(d => d.slice(0, 3)).join(', ')}
                                                </p>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleDeleteReminder(reminder.supplement_id)}
                                                className="text-destructive"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
